/* lifecycleStages.js
 *
 * The 5-macro-stage lifecycle navigation primitive that sits IN FRONT
 * of the existing 6-project-tile picker on the homepage hero.
 *
 * Architecture: Stage button -> LifecycleStageModal opens with content
 * curated for that stage -> user clicks a project tile inside the
 * modal -> closes lifecycle modal, opens the existing JourneyModal
 * for that journey (preserving the entire existing flow downstream).
 *
 * STATE-AWARENESS (2026-06):
 *   BuildPilot is live in SA + VIC. Links that point at a city/state
 *   specific page carry `local: true`. They may also carry a `vic`
 *   override `{ label, href }` with the Melbourne equivalent.
 *   `localizeStage(stage, stateCode)` resolves content per state:
 *     - SA        -> local links use their default (Adelaide) href/label
 *     - VIC       -> local links use `vic` if present, else are dropped
 *     - waitlist  -> all local links dropped; national links kept; the
 *                    modal shows a "coming soon to <State>" note.
 *   National links (no `local`) always show. Build-type links and
 *   /learn/* finishes are national (shared routes).
 *
 * `projectTiles` are JOURNEY SLUGS from /data/journeyConfig.js.
 */

import { LIVE_STATES, STATE_NAMES } from '../lib/userState';

// National build-type taxonomy (/learn/build-type/:slug is a shared
// route). Surfaced as chips inside the Dream / Plan / Choose stages so
// users can branch into "what kind of build is right for me".
const BUILD_TYPES_SECTION = {
  title: 'Explore build types',
  links: [
    { label: 'Custom', href: '/learn/build-type/custom' },
    { label: 'Volume', href: '/learn/build-type/volume' },
    { label: 'Luxury', href: '/learn/build-type/luxury' },
    { label: 'Budget', href: '/learn/build-type/budget' },
    { label: 'Knockdown rebuild', href: '/learn/build-type/knockdown' },
    { label: 'Two-storey', href: '/learn/build-type/two-storey' },
    { label: 'Townhouse', href: '/learn/build-type/townhouse' },
    { label: 'Granny flat', href: '/learn/build-type/granny-flat' },
    { label: 'Country', href: '/learn/build-type/country' },
    { label: 'Acreage', href: '/learn/build-type/acreage' },
    { label: 'First home', href: '/learn/build-type/first-home' },
    { label: 'Compare build types', href: '/learn/build-type/compare' },
  ],
};

export const LIFECYCLE_STAGES = [
  // ──────────────────────────────────────────────────────────────────
  {
    slug: 'dream',
    name: 'Start Dreaming',
    emoji: '☁️',
    iconSrc: '/images/icons/journey/dream.webp',
    heading: 'Just looking around?',
    intro: 'Browse home designs, save favourites and picture what your home could look like.',
    emotion: 'You are not ready to buy, build or borrow. You are just trying to figure out what any of this could look like for you.',
    ageWhisper: 'Usually late-teens through twenties',
    heroImage: '/images/heroes/home%20design.webp?v=2',
    projectTiles: ['build-a-home', 'renovate-and-extend', 'improve-my-home', 'invest-and-develop'],
    matcher: { label: 'Match me to a floor plan', event: 'buildpilot:open-home-designs-matcher' },
    sections: [
      {
        title: "Browse builders' home designs",
        links: [
          { label: '760+ floor plans', href: '/designs' },
          { label: 'Designs by builder', href: '/home-designs' },
          { label: 'Display home guide', href: '/display-home-guide' },
          { label: 'New estates', href: '/estates' },
          { label: 'Award-winning builds', href: '/awards' },
          { label: 'Real case studies', href: '/case-studies' },
          { label: 'Adelaide builders', href: '/adelaide-builders-index', local: true, vic: { label: 'Melbourne builders', href: '/home-builders-melbourne' } },
          { label: 'Country home builders SA', href: '/country-home-builders-south-australia', local: true },
        ],
      },
      {
        title: 'What kind of home?',
        links: [
          { label: 'Build a new home', href: '/building-a-new-home' },
          { label: 'Renovate', href: '/renovations' },
          { label: 'Choosing land', href: '/choosing-land' },
        ],
      },
      BUILD_TYPES_SECTION,
      {
        title: 'Workspace · save your inspirations',
        workspaceToolIds: ['favourite-homes', 'facade-visualiser', 'furniture-planner'],
      },
      {
        title: 'Light reading',
        links: [
          { label: 'Blog', href: '/blog' },
          { label: 'Glossary', href: '/terminology' },
          { label: 'How BuildPilot works', href: '/how-it-works' },
          { label: 'How we make money', href: '/about/how-we-make-money' },
          { label: 'Build vs buy Adelaide', href: '/build-vs-buy-adelaide', local: true },
          { label: 'First home buyer Adelaide', href: '/first-home-buyer-adelaide', local: true, vic: { label: 'First home buyer Melbourne', href: '/first-home-buyer-melbourne' } },
          { label: 'Community', href: '/community' },
          { label: 'Take the builder quiz', href: '/builder-quiz' },
        ],
      },
    ],
    softCta: { label: 'Not sure where to start? 15-min free advisor chat', href: '/one-on-one-support' },
  },
  // ──────────────────────────────────────────────────────────────────
  {
    slug: 'plan',
    name: 'Begin Planning',
    emoji: '📐',
    iconSrc: '/images/icons/journey/plan.webp',
    heading: 'Time to plan the real numbers',
    intro: 'Map the budget, find the right block, lock in the project type.',
    emotion: 'You know you want to own something one day. The number is intimidating and the timeline is fuzzy. This is where it starts to feel real.',
    ageWhisper: 'Usually twenties through early-thirties',
    heroImage: '/images/heroes/choosing%20land.webp?v=2',
    projectTiles: ['build-a-home', 'renovate-and-extend', 'invest-and-develop'],
    matcher: { label: 'Match me to a house & land package', event: 'buildpilot:open-hnl-matcher' },
    sections: [
      {
        title: 'Land & estates',
        links: [
          { label: 'House & land Adelaide', href: '/house-and-land-adelaide', local: true, vic: { label: 'House & land Melbourne', href: '/house-and-land-melbourne' } },
          { label: 'Land for sale Adelaide', href: '/land-for-sale-adelaide', local: true },
          { label: 'New estates', href: '/estates' },
          { label: 'Cheapest land in SA', href: '/help/cheapest-land-south-australia', local: true },
          { label: 'Cheapest land right now', href: '/help/cheapest-land-to-buy-right-now' },
          { label: 'Buy land off SA Government', href: '/help/buy-land-off-sa-government', local: true },
          { label: 'Growth suburbs Adelaide', href: '/growth-suburbs-adelaide', local: true, vic: { label: 'Growth suburbs Melbourne', href: '/growth-suburbs-melbourne' } },
          { label: 'Adelaide suburbs that will boom', href: '/help/which-adelaide-suburbs-will-boom', local: true, vic: { label: 'Best suburbs to build Melbourne', href: '/best-suburbs-to-build-melbourne' } },
          { label: 'Are house prices falling Adelaide', href: '/help/are-house-prices-falling-adelaide', local: true },
        ],
      },
      {
        title: 'Cost & budget',
        links: [
          { label: 'Land & Build repayment calculator', href: '/p/UAklUjJ9rbMGQITCzutU5tSrPtMF_ky9_2L4aP4TScs' },
          { label: 'Building costs Adelaide', href: '/building-costs-adelaide', local: true, vic: { label: 'Building costs Melbourne', href: '/melbourne-building-costs' } },
          { label: 'Costs by bedrooms', href: '/house-building-costs-by-bedroom-adelaide', local: true },
          { label: 'Build times Adelaide', href: '/build-times-adelaide', local: true, vic: { label: 'Build times Melbourne', href: '/how-long-does-it-take-to-build-a-house-in-melbourne' } },
          { label: 'What can $500k build?', href: '/what-can-you-build-for-500k-adelaide', local: true },
          { label: 'What can $600k build?', href: '/what-can-you-build-for-600k-adelaide', local: true },
          { label: 'What can $650k build?', href: '/what-can-you-build-for-650k-adelaide', local: true },
          { label: 'Cost to build Adelaide', href: '/help/cost-to-build-house-adelaide', local: true, vic: { label: 'Cost to build Melbourne', href: '/how-much-does-it-cost-to-build-a-house-in-melbourne' } },
          { label: 'Most expensive part of a build', href: '/help/most-expensive-part-of-building-a-house' },
          { label: 'Build a house for $600k', href: '/help/build-house-for-600000' },
          { label: 'How much for H&L?', href: '/help/how-much-money-house-and-land-package' },
          { label: 'Are H&L packages worth it?', href: '/help/house-and-land-packages-worth-it' },
        ],
      },
      BUILD_TYPES_SECTION,
      {
        title: 'House & land specifics',
        links: [
          { label: 'H&L under $400k', href: '/house-and-land-packages-adelaide-under-400k', local: true },
          { label: 'H&L under $500k', href: '/house-and-land-packages-adelaide-under-500k', local: true },
          { label: 'H&L under $600k', href: '/house-and-land-packages-adelaide-under-600k', local: true },
          { label: 'H&L under $700k', href: '/house-and-land-packages-adelaide-under-700k', local: true },
          { label: 'H&L vs buying separately', href: '/house-and-land-vs-buying-separately' },
          { label: 'Turnkey vs standard', href: '/turnkey-vs-standard-packages' },
          { label: 'H&L hidden costs', href: '/house-and-land-package-hidden-costs' },
          { label: 'H&L red flags', href: '/house-and-land-red-flags' },
          { label: 'Questions to ask H&L', href: '/questions-to-ask-house-and-land' },
          { label: 'FHB H&L guide', href: '/first-home-buyer-house-and-land-guide' },
        ],
      },
      {
        title: 'Approvals & siting',
        links: [
          { label: 'Energy efficient home', href: '/energy-efficient-home-guide' },
          { label: 'Council approvals (Melbourne)', href: '/what-council-approvals-do-i-need-melbourne', vicOnly: true },
          { label: 'Building in Onkaparinga', href: '/building-in-onkaparinga-council', local: true, vic: { label: 'Building in Whittlesea', href: '/vic/building-in/whittlesea-council-vic' } },
          { label: 'Building in Playford', href: '/building-in-playford-council', local: true, vic: { label: 'Building in Hume', href: '/vic/building-in/hume-council-vic' } },
          { label: 'Building in Salisbury', href: '/building-in-salisbury-council', local: true, vic: { label: 'Building in Frankston', href: '/vic/building-in/frankston-council-vic' } },
          { label: 'Building in Kingston (VIC)', href: '/vic/building-in/kingston-council-vic', vicOnly: true },
          { label: 'Building in Boroondara (VIC)', href: '/vic/building-in/boroondara-council-vic', vicOnly: true },
        ],
      },
      {
        title: 'Knockdown rebuild track',
        links: [
          { label: 'KDR overview', href: '/knockdown-rebuild' },
          { label: 'KDR full guide', href: '/knockdown-rebuild-guide' },
          { label: 'KDR costs', href: '/knockdown-rebuild/costs' },
          { label: 'KDR council approvals', href: '/knockdown-rebuild/council-approvals' },
          { label: 'When to KDR vs renovate', href: '/knockdown-rebuild/when-to-knockdown-vs-renovate' },
        ],
      },
      {
        title: 'Granny flats',
        links: [
          { label: 'Granny flat guide', href: '/granny-flat-guide' },
        ],
      },
      {
        title: 'Workspace · plan with real numbers',
        workspaceToolIds: ['cost-tracker', 'house-and-land', 'solar-orientation-guide', 'government-resources'],
      },
    ],
    softCta: { label: 'Stuck on the budget? Free 15-min finance review with Equable Home Loans', href: '/p/UAklUjJ9rbMGQITCzutU5tSrPtMF_ky9_2L4aP4TScs?token=eq_bp_2026_UAklUjJ9rbMGQITCzutU5tSrPtMF' },
  },
  // ──────────────────────────────────────────────────────────────────
  {
    slug: 'choose',
    name: 'Make Choices',
    emoji: '🎯',
    iconSrc: '/images/icons/journey/choose.webp',
    heading: "Let's get you matched",
    intro: "60 seconds to brief us. We'll line up the right builder, designer, supplier or tradie.",
    emotion: 'You have your finance, your land or your plan. You just need people who can actually do what you want, in your budget, on your block.',
    ageWhisper: 'Usually late-twenties through forties',
    heroImage: '/images/heroes/new%20homes.webp?v=2',
    projectTiles: ['build-a-home', 'renovate-and-extend', 'improve-my-home', 'invest-and-develop'],
    matchers: [
      { label: 'Shortlist a builder', sourcePlacement: 'lifecycle:choose:builders' },
      { label: 'Shortlist a tradie', sourcePlacement: 'lifecycle:choose:tradies' },
      { label: 'Shortlist a supplier', sourcePlacement: 'lifecycle:choose:suppliers' },
      { label: 'Shortlist a professional', sourcePlacement: 'lifecycle:choose:pros' },
      { label: 'Shortlist a floor plan', event: 'buildpilot:open-home-designs-matcher' },
      { label: 'Shortlist a H&L package', event: 'buildpilot:open-hnl-matcher' },
    ],
    sections: [
      {
        title: 'Builder selection helpers',
        links: [
          { label: 'Top 10 home builders Adelaide', href: '/home-builders-adelaide', local: true, vic: { label: 'Home builders Melbourne', href: '/home-builders-melbourne' } },
          { label: 'Top 5 home builders Adelaide', href: '/top-5-home-builders-adelaide', local: true },
          { label: 'Budget home builders Adelaide', href: '/budget-home-builders-adelaide', local: true },
          { label: 'Best builder for FHB', href: '/best-builder-for-first-home-buyers' },
          { label: 'Best for two-storey', href: '/best-builder-for-two-storey-homes' },
          { label: 'Best for KDR', href: '/best-builder-for-knockdown-rebuild' },
          { label: 'Volume vs project builder', href: '/volume-builder-vs-project-builder-adelaide', local: true, vic: { label: 'Melbourne builder types', href: '/melbourne-home-builder-types' } },
          { label: 'Volume vs custom builder', href: '/volume-builder-vs-custom-builder-adelaide', local: true },
          { label: 'Project vs custom builder', href: '/project-builder-vs-custom-builder-adelaide', local: true },
          { label: 'Compare hub', href: '/compare' },
          { label: 'Builder selection checklist', href: '/builder-selection-checklist' },
          { label: 'Take the builder quiz', href: '/builder-quiz' },
          { label: 'Questions to ask builders', href: '/questions-to-ask-builders' },
          { label: 'Builders index', href: '/adelaide-builders-index', local: true },
          { label: 'Country home builders SA', href: '/country-home-builders-south-australia', local: true },
          { label: 'Home builders near me', href: '/home-builders-near-me' },
        ],
      },
      {
        title: 'Best-of editorial',
        links: [
          { label: 'Best home builder in SA', href: '/help/best-home-builder-south-australia', local: true },
          { label: 'Most reliable builder Adelaide', href: '/help/most-reliable-builder-adelaide', local: true },
          { label: 'Top 5 construction companies', href: '/help/top-5-construction-companies' },
          { label: 'Best reputation builder', href: '/help/which-house-builder-has-the-best-reputation' },
        ],
      },
      {
        title: 'Pros & trades',
        links: [
          { label: 'Hiring professionals guide', href: '/hiring-professionals-guide' },
          { label: 'Hiring trades guide', href: '/hiring-trades-guide' },
          { label: 'Building inspectors', href: '/building-inspectors', local: true, vic: { label: 'Building inspectors Melbourne', href: '/building-inspectors-melbourne' } },
        ],
      },
      {
        title: 'Finishes & materials',
        links: [
          { label: 'Tiles', href: '/learn/tiles' },
          { label: 'Flooring', href: '/learn/flooring' },
          { label: 'Kitchens', href: '/learn/kitchens' },
          { label: 'Bathrooms', href: '/learn/bathrooms' },
          { label: 'Stone & benchtops', href: '/learn/stone' },
          { label: 'Windows & doors', href: '/learn/windows-doors' },
          { label: 'Roofing', href: '/learn/roofing' },
          { label: 'Plumbing', href: '/learn/plumbing' },
          { label: 'Electrical', href: '/learn/electrical' },
          { label: 'Cabinetry', href: '/learn/cabinetry' },
          { label: 'Lighting', href: '/learn/lighting' },
          { label: 'Landscaping', href: '/learn/landscaping' },
        ],
      },
      BUILD_TYPES_SECTION,
      {
        title: 'Workspace · the matching tools',
        workspaceToolIds: ['find-a-builder', 'find-a-tradie', 'find-a-supplier', 'quote-comparison', 'builder-licence-check', 'building-insurance-check', 'contacts-hub'],
      },
    ],
    softCta: { label: 'Got 3 quotes and feeling overwhelmed? 15-min independent review', href: '/one-on-one-support' },
  },
  // ──────────────────────────────────────────────────────────────────
  {
    slug: 'build',
    name: 'Get Building',
    emoji: '🏗️',
    iconSrc: '/images/icons/journey/build.webp',
    heading: "On the tools - let's keep your build on track",
    intro: 'Track the budget, log every milestone, scan for defects, and call in help when something is not right.',
    emotion: 'You signed. Now variations are stacking up, decisions are piling on, and something feels off with the builder. It is louder in your head than anyone warned you.',
    ageWhisper: 'A season, not an age - this feels the same at 28 or 58',
    heroImage: '/images/heroes/changing%20builders.webp?v=2',
    projectTiles: ['my-build-needs-help', 'renovate-and-extend'],
    matchers: [
      { label: 'Find an independent inspector', sourcePlacement: 'lifecycle:build:inspector' },
      { label: 'Find a property lawyer', sourcePlacement: 'lifecycle:build:lawyer' },
      { label: 'Find a tradie for finishing jobs', sourcePlacement: 'lifecycle:build:tradie' },
      { label: 'Find a supplier for late selections', sourcePlacement: 'lifecycle:build:supplier' },
    ],
    sections: [
      {
        title: 'Workspace · tools for the live build',
        workspaceToolIds: ['cost-tracker', 'milestones', 'checklists', 'contacts-hub', 'documents', 'defects-scanner', 'project-health'],
      },
      {
        title: 'When things go sideways',
        links: [
          { label: 'Building disputes hub', href: '/help/building-disputes' },
          { label: 'Common builder problems', href: '/common-builder-problems' },
          { label: 'Builder went bankrupt - what now?', href: '/builder-went-bankrupt-what-now' },
          { label: 'Complaints process', href: '/legal/complaints-process' },
          { label: 'Find an inspector', href: '/building-inspectors', local: true, vic: { label: 'Find an inspector (Melbourne)', href: '/building-inspectors-melbourne' } },
        ],
      },
      {
        title: 'KDR mid-build',
        links: [
          { label: 'Demolition process', href: '/knockdown-rebuild/demolition-process' },
        ],
      },
    ],
    softCta: { label: "Something feels off? Talk to our advisor team", href: '/one-on-one-support' },
  },
  // ──────────────────────────────────────────────────────────────────
  {
    slug: 'own',
    name: 'Love Living',
    emoji: '🏡',
    iconSrc: '/images/icons/journey/own.webp',
    heading: "Welcome home - let's keep it working for you",
    intro: 'From handover defects in year one to renovating, refinancing or selling later - BuildPilot stays with you.',
    emotion: 'The keys are yours. There is a defect list you have not started, an appliance drawer full of manuals, and one day a whisper that the house is not quite fitting life any more.',
    ageWhisper: 'Anywhere from your first year in through year ten and beyond',
    heroImage: '/images/heroes/decking.webp?v=2',
    projectTiles: ['improve-my-home', 'renovate-and-extend', 'invest-and-develop'],
    matchers: [
      { label: 'Find a tradie for repairs', sourcePlacement: 'lifecycle:own:tradie' },
      { label: 'Find an inspector', sourcePlacement: 'lifecycle:own:inspector' },
      { label: 'Find a conveyancer', sourcePlacement: 'lifecycle:own:conveyancer' },
      { label: 'Find a finance broker', sourcePlacement: 'lifecycle:own:broker' },
    ],
    sections: [
      {
        title: 'Workspace · tools for ownership',
        workspaceToolIds: ['documents', 'cost-tracker', 'find-a-tradie', 'defects-scanner', 'building-insurance-check', 'find-a-builder', 'favourite-homes'],
      },
      {
        title: 'When ready to do it all again',
        links: [
          { label: 'Case studies', href: '/case-studies' },
          { label: 'Energy efficient retrofit', href: '/energy-efficient-home-guide' },
          { label: 'Granny flat / addition', href: '/granny-flat-guide' },
          { label: 'Renovate vs rebuild', href: '/knockdown-rebuild/when-to-knockdown-vs-renovate' },
          { label: 'Renovations overview', href: '/renovations' },
          { label: 'Community', href: '/community' },
          { label: 'Blog', href: '/blog' },
        ],
      },
    ],
    softCta: { label: 'Thinking of doing it all again? Free advisor chat', href: '/one-on-one-support' },
  },
];

// Resolve a single link for a given state. Returns a plain
// { label, href } or null (link should be dropped for this state).
//   - vicOnly : shown only to VIC visitors (dropped for SA + waitlist)
//   - local   : SA page; shown to SA, swapped to `vic` for VIC (or
//               dropped if no `vic`), dropped for waitlist states
//   - neither : national link, shown to everyone
function resolveLink(link, { isVIC, isWaitlist }) {
  if (link.vicOnly) return isVIC ? { label: link.label, href: link.href } : null;
  if (!link.local) return { label: link.label, href: link.href };
  if (isWaitlist) return null;              // waitlist: drop local links
  if (isVIC) return link.vic ? { label: link.vic.label, href: link.vic.href } : null;
  return { label: link.label, href: link.href }; // SA (default)
}

/**
 * Localize a stage's content for the visitor's state. Drops SA-only
 * links for VIC/waitlist, swaps in Melbourne equivalents for VIC, and
 * removes any section left empty. Adds `waitlist`, `stateName` and
 * `hasLocalContent` flags for the modal to render a "coming soon" note.
 */
export function localizeStage(stage, stateCode = 'SA') {
  if (!stage) return stage;
  const isVIC = stateCode === 'VIC';
  const isWaitlist = !LIVE_STATES.has(stateCode);
  const ctx = { isVIC, isWaitlist };

  let hasLocalContent = false;
  const sections = (stage.sections || [])
    .map((section) => {
      if (section.workspaceToolIds || !section.links) return section;
      if (section.links.some((l) => l.local)) hasLocalContent = true;
      const links = section.links.map((l) => resolveLink(l, ctx)).filter(Boolean);
      return links.length ? { ...section, links } : null;
    })
    .filter(Boolean);

  return {
    ...stage,
    sections,
    waitlist: isWaitlist,
    hasLocalContent,
    stateName: STATE_NAMES[stateCode] || null,
  };
}

// Look up a stage by slug, optionally localized to a state.
export const getLifecycleStage = (slug, stateCode) => {
  const stage = LIFECYCLE_STAGES.find((s) => s.slug === slug) || null;
  return stateCode ? localizeStage(stage, stateCode) : stage;
};
